'use strict'

import React from 'react'
import PropTypes from 'prop-types'
import {
    Grid,
    HStack,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'

import PDComponent from '../../pd-component'
import PDButton from '../../components/pd-button'
import {
    PD_HERO_PROMOTION_BUY_X_FOR_Y,
    PD_HERO_PROMOTION_PERCENT_OFF,
    PD_HERO_PROMOTION_UP_TO_PERCENT_OFF
} from '../../../../constants'

export const PromoPairableStyles = ({regions, ...rest}) => {
    const {showTitle, isButtonBelowTilesMobile, typeId, name} = rest

    // Find the target promo component (one of the Hero Promotion types)
    const target = regions
        ?.flatMap((r) => r.components)
        ?.flatMap((c) => c.regions ?? [])
        ?.flatMap((r) => r.components ?? [])
        ?.find((c) =>
            [
                PD_HERO_PROMOTION_PERCENT_OFF,
                PD_HERO_PROMOTION_BUY_X_FOR_Y,
                PD_HERO_PROMOTION_UP_TO_PERCENT_OFF
            ].includes(c.typeId)
        )

    // Destructure button and CTA data from the found promo component
    const {
        buttonText1,
        buttonLink1,
        buttonTextColor1,
        buttonBgColor1,
        buttonFontFamily,
        buttonText2,
        buttonLink2,
        buttonTextColor2,
        buttonBgColor2,
        ctaTypeMobile,
        ctaTypeDesktop,
        dropdownLinkLabel1,
        dropdownLinkUrl1,
        dropdownLinkLabel2,
        dropdownLinkUrl2,
        dropdownLinkLabel3,
        dropdownLinkUrl3,
        dropdownLinkLabel4,
        dropdownLinkUrl4,
        dropdownLinkLabel5,
        dropdownLinkUrl5,
        dropdownLinkLabel6,
        dropdownLinkUrl6
    } = target?.data || {}

    // Flags for presence of button(s)
    const hasButton1 = buttonText1 && buttonLink1
    const hasButton2 = buttonText2 && buttonLink2

    // Choose CTA type based on current breakpoint
    const ctaType1 = useBreakpointValue({base: ctaTypeMobile, md: ctaTypeDesktop})
    const ctaType2 = useBreakpointValue({base: ctaTypeMobile, md: ctaTypeDesktop})

    // Prepare dropdown link list for the first button
    const dropdownLinks1 = [
        dropdownLinkLabel1 &&
            dropdownLinkUrl1 && {label: dropdownLinkLabel1, url: dropdownLinkUrl1},
        dropdownLinkLabel2 &&
            dropdownLinkUrl2 && {label: dropdownLinkLabel2, url: dropdownLinkUrl2},
        dropdownLinkLabel3 &&
            dropdownLinkUrl3 && {label: dropdownLinkLabel3, url: dropdownLinkUrl3},
        dropdownLinkLabel4 &&
            dropdownLinkUrl4 && {label: dropdownLinkLabel4, url: dropdownLinkUrl4},
        dropdownLinkLabel5 &&
            dropdownLinkUrl5 && {label: dropdownLinkLabel5, url: dropdownLinkUrl5},
        dropdownLinkLabel6 && dropdownLinkUrl6 && {label: dropdownLinkLabel6, url: dropdownLinkUrl6}
    ].filter(Boolean)

    const analyticsProps = {
        componentType: rest.typeId,
        componentName: rest.name || rest.typeId
    }

    return (
        <Grid
            gap={{lg: '14px'}}
            templateColumns={{base: '1fr', lg: '400px 1fr'}}
            gridTemplateRows={{base: 'auto auto', lg: '1fr'}}
            aspectRatio="auto"
            alignItems="center"
            sx={{
                // Grid item and swiper styles
                '& .promo-grid-item': {maxHeight: '400px'},
                '.items-count, .text-price, .dollar-sign, .title-text': {
                    fontSize: '72px !important'
                },
                '.for-text': {
                    fontSize: '48px !important'
                },
                '.eyebrow-text, .sub-title-text': {
                    fontSize: '24px !important'
                },
                '.text-stack': {
                    padding: '20px !important'
                },
                '.list-title': {display: showTitle ? 'block' : 'none'},
                '.swiper-wrapper': {paddingLeft: '0px !important'},
                'div[data-testid="product-scroller"]': {padding: {base: '0px', lg: '20px 0px'}},
                '.size-swatch, .disabled-swatch': {
                    width: 'fit-content !important',
                    minWidth: '35px !important',
                    height: '35px !important',
                    margin: '2.5px'
                },
                '.sizes-wrapper': {
                    width: '100%',
                    margin: '0px',
                    padding: '5px',
                    gap: '5px !important',
                    borderTopRightRadius: '5px',
                    borderTopLeftRadius: '5px',
                    button: {
                        paddingLeft: '10px !important',
                        paddingRight: '10px !important'
                    }
                },
                '.swiper-parent': {
                    width: {lg: 'calc(100vw - 430px)'},
                    '.swiper': {padding: '20px 0px'}
                },
                '.swiper-button-prev, .swiper-button-next': {
                    top: {lg: '120px !important'},
                    boxShadow: 'none !important'
                },
                '.swiper-slide': {
                    marginRight: '10px !important',
                    width: {lg: '175px !important'},
                    img: {
                        aspectRatio: '175 / 250 !important'
                    }
                },
                '.swiper-slide .image-wrapper .mobile-image, .swiper-slide .image-wrapper .product-tile-image':
                    {
                        img: {
                            objectFit: 'cover'
                        }
                    },
                // Hide promo buttons inside promo section on mobile if set to show below tiles
                '.promo-grid-item .promo-buttons-wrapper': {
                    display: {base: isButtonBelowTilesMobile ? 'none' : 'flex', lg: 'flex'}
                },
                '.promo-button, .promo-dropdown button, .promo-dropdown a': {
                    fontSize: '14px !important'
                },
                '.skeleton-tile-image': {
                    width: '175px',
                    height: '250px'
                },
                '.skeleton-box': {
                    width: '175px'
                },
                'div[role="radiogroup"]': {
                    display: 'none'
                },
                '.grid-box': {
                    padding: '70px 0px !important'
                },
                '& > div': {
                    minWidth: 0
                }
            }}
        >
            {/* Render all subcomponents inside each region */}
            {regions?.map((region) =>
                region?.components?.map((component, index) => (
                    <PDComponent
                        key={`${region.id}-${index}`}
                        component={component}
                        variant="promoGridTwoItems"
                        analyticsProps={analyticsProps}
                    />
                ))
            )}

            {/* Render promo buttons BELOW product tiles on mobile (if enabled) */}
            {(hasButton1 || hasButton2) && isButtonBelowTilesMobile && (
                <HStack
                    gap="20px"
                    justifyContent="center"
                    flexWrap="wrap"
                    margin="10px 0 30px 0"
                    display={{base: 'flex', lg: 'none'}}
                    className="promo-buttons-wrapper"
                >
                    {/* First Button */}
                    {hasButton1 && (
                        <PDButton
                            text={buttonText1}
                            url={buttonLink1}
                            color={buttonTextColor1}
                            ctaType={ctaType1}
                            buttonBackgroundColor={buttonBgColor1}
                            buttonFontFamily={buttonFontFamily}
                            links={dropdownLinks1}
                            typeId={typeId}
                            name={name}
                        />
                    )}

                    {/* Second Button */}
                    {hasButton2 && (
                        <PDButton
                            text={buttonText2}
                            url={buttonLink2}
                            color={buttonTextColor2}
                            ctaType={ctaType2}
                            buttonBackgroundColor={buttonBgColor2}
                            buttonFontFamily={buttonFontFamily}
                            typeId={typeId}
                            name={name}
                        />
                    )}
                </HStack>
            )}
        </Grid>
    )
}

// Type checking for regions
PromoPairableStyles.propTypes = {
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.shape({
                        question: PropTypes.string,
                        answer: PropTypes.string
                    })
                })
            )
        })
    ).isRequired
}

export default PromoPairableStyles
